import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded"
import NoteAddIcon from "@mui/icons-material/NoteAdd"
import SaveAsRoundedIcon from "@mui/icons-material/SaveAsRounded"
import SaveRoundedIcon from "@mui/icons-material/SaveRounded"
import {
    Button,
    ClickAwayListener,
    Divider,
    Grid,
    Grow,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Skeleton,
    Tooltip,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { isWindows } from "react-device-detect"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import type { AnySceneObjectT, CanvasI } from "tweenly"
import config from "../config"
import { UserRole } from "../constants/roles.enum"
import { Status } from "../constants/status"
import { selectCanRedo, selectCanUndo, selectUndoable } from "../helpers/selector.helpers"
import { useCanvas } from "../hooks/useCanvas"
import { useCheckEditor } from "../hooks/useCheckEditor"
import useHTMLFileContent from "../hooks/useHTMLFileContent"
import { useAppDispatch, useAppSelector, useAppStore } from "../hooks/useRedux"
import { useSession } from "../hooks/useSession"
import { useVerifyPermission } from "../hooks/useVerifyPermission"
import { useWorkspacePermission } from "../hooks/useWorkspacePermission"
import { getCompanyStatsAsync } from "../store/slices/companyStats.slice"
import {
    clearAndReset,
    prevSaveFile,
    setIsTimelineSettingChanged,
    setOpenAlertTestGraphicDialog,
    setOpenCanvasSettings,
    setOpenDeleteAlert,
    setOpenDialogCreateNew,
    setOpenDialogToDuplicate,
    setOpenSaveForm,
    setOpenVersionHistoryDetail,
    setSignSaveAs,
} from "../store/slices/editor.slice"
import { GraphicT, deleteGraphicAsync, updateGraphicAsync } from "../store/slices/graphic.slice"
import { AssetRef } from "../store/slices/graphics.slice"
import { selectSortedRootObjects } from "../store/slices/objects.slice"
import { getSimilarGraphicAsync } from "../store/slices/similarGraphics.slice"
import AlertDialog from "./common/AlertDialog"
import AlertMoreChoiceDialog from "./common/AlertMoreChoiceDialog"
import ButtonWithTooltip from "./common/ButtonWithTooltip"
import LoadingBackdrop from "./common/LoadingBackdrop"
import LoadingButton from "./common/LoadingButton"
import AlertTestGraphicDialog from "./Editor/TopNavbar/AlertTestGraphicDialog"
import ClearScene from "./Editor/TopNavbar/ClearScene"
import DeleteGraphic from "./Editor/TopNavbar/DeleteGraphic"
import DownloadFile from "./Editor/TopNavbar/DownloadFile"
import ShowVersionHistory from "./Editor/TopNavbar/ShowVersionHistory"
import TestGraphic from "./Editor/TopNavbar/TestGraphic"
import UploadFile from "./Editor/TopNavbar/UploadFile"

const TopNavbarEditorActions = () => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const location = useLocation()
    const { t } = useTranslation()
    const { processFile } = useHTMLFileContent()
    const [openMenu, setOpenMenu] = useState<boolean>(false)
    const [openLockedDialog, setOpenLockedDialog] = useState(false)
    const [openLockedSaveDialog, setOpenLockedSaveDialog] = useState(false)
    const openDelAlert = useAppSelector((state) => state.editor.value.openDeleteAlert)
    const openDialogToDuplicate = useAppSelector(
        (state) => state.editor.value.openDialogToDuplicate
    )
    const openDialogCreateNew = useAppSelector((state) => state.editor.value.openDialogCreateNew)
    const anchorRef = useRef<HTMLButtonElement>(null)

    const { openMenuWizard } = useAppSelector((state) => state.editor.value)
    const { disabledShortSave } = useCheckEditor()
    const canUndo = useAppSelector((state) => selectCanUndo(state))
    const canRedo = useAppSelector((state) => selectCanRedo(state))
    const graphic = useAppSelector((state) => state.graphic)
    const store = useAppStore()
    const hasNoObjects = useAppSelector(
        (state) => selectSortedRootObjects(selectUndoable(state).objects).length === 0
    )
    const getRootObjects = (): AnySceneObjectT[] =>
        selectSortedRootObjects(selectUndoable(store.getState()).objects)

    const isClearSceneDisabled = hasNoObjects

    const { canvasResolution: canvas } = useCanvas()

    const assets = useAppSelector((state) => state.assets.data)

    const { canDuplicate, canExport, canExportPackage, isFreeLicense } = useVerifyPermission(
        graphic.data,
        graphic.data.company
    )
    const { expiredOwnerCompanyLicense, expiredParticipantCompanyLicense } = useWorkspacePermission(
        graphic.data.company
    )
    const { hasRole } = useSession()

    useEffect(() => {
        if (graphic.data.isFinal) {
            //this denied access for editing in editor
            setOpenLockedDialog(true)
        } else if (graphic.status === Status.GRAPHIC_IS_FINAL) {
            //this show alertDialog when graphic was changed by another user and disabled the save action
            setOpenLockedSaveDialog(true)
        }
    }, [graphic.status, graphic.data.isFinal])

    const prevOpen = useRef(openMenu)
    useEffect(() => {
        prevOpen.current = openMenu
    }, [openMenu])

    const handleToggle = () => {
        setOpenMenu((prevOpen) => !prevOpen)
    }

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) return

        setOpenMenu(false)
    }

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Tab") {
            event.preventDefault()
            setOpenMenu(false)
        } else if (event.key === "Escape") {
            setOpenMenu(false)
        }
    }

    // Process and download HTML + JSON file Save As
    const processSave = async () => {
        const file = processFile()
        setOpenMenu(false)
        dispatch(prevSaveFile(file))
        dispatch(setOpenSaveForm(true))
    }

    // functionality when save as new / (Duplicate)
    const handleDuplicate = async () => {
        if (canUndo) {
            dispatch(setOpenDialogToDuplicate(true))
        }
        processSave()
        const companyId = graphic.data.companyId
        const regex = "^" + graphic.data.name.replace(/(\()(\d+)(\))$/, () => "")
        //warning if more data use useEffect or modify slice
        await dispatch(getSimilarGraphicAsync(companyId, regex))
        dispatch(setOpenVersionHistoryDetail(false))
        dispatch(setSignSaveAs(true))
    }

    // functionality when regular save
    const handleOpenSaveForm = async () => {
        processSave()
        dispatch(setSignSaveAs(false))
    }
    const handleSaveShortCall = async (
        data: GraphicT,
        canvas: CanvasI,
        objects: AnySceneObjectT[],
        reset: boolean
    ) => {
        const file = processFile()
        let assetsRef: AssetRef[] = assets.map((asset) => ({ id: asset._id }))
        setOpenMenu(false)
        await dispatch(prevSaveFile(file))
        await dispatch(updateGraphicAsync({ ...data, assetsRef }, file, reset))
        await dispatch(setIsTimelineSettingChanged(false))
        await dispatch(getCompanyStatsAsync(data.companyId, true))
        return true
    }
    const handleCloseAlert3ChoiceDialog = () => {
        dispatch(setOpenDialogToDuplicate(false))
        dispatch(setOpenDialogCreateNew(false))
        dispatch(setOpenSaveForm(false))
        dispatch(setOpenVersionHistoryDetail(false))
    }

    const navigate = useNavigate()

    const handleCreateNew = async () => {
        if (!canUndo && !canRedo) {
            setOpenMenu(false)
            createNewScene()
        } else {
            setOpenMenu(false)
            dispatch(setOpenDialogCreateNew(true))
            dispatch(setOpenSaveForm(false))
            dispatch(setOpenVersionHistoryDetail(false))
        }
    }
    const createNewScene = async () => {
        await dispatch(clearAndReset(graphic.data._id))
        navigate("/app/editor")
        dispatch(setOpenDialogCreateNew(false))
    }

    const handleContinueWithoutSaving = async () => {
        if (openDialogToDuplicate) {
            dispatch(setOpenDialogToDuplicate(false))
        } else if (openDialogCreateNew) {
            createNewScene()
            dispatch(setOpenCanvasSettings(true))
        }
    }
    const handleSaveAndContinue = async (
        data: GraphicT,
        canvas: CanvasI,
        objects: AnySceneObjectT[]
    ) => {
        //save changes and continues in Duplicate GraphicSaveForm
        if (openDialogToDuplicate) {
            const isSaved = await handleSaveShortCall(data, canvas, objects, false)
            if (isSaved) {
                dispatch(setOpenDialogToDuplicate(false))
            }
            return
        }
        //save changes and create new scene
        else if (openDialogCreateNew) {
            const isSaved = await handleSaveShortCall(data, canvas, objects, true)
            if (isSaved) {
                createNewScene()
                dispatch(setOpenCanvasSettings(true))
            }
            return
        }
    }
    const handleAgreeAlertDialog = () => {
        if (openDialogCreateNew) {
            createNewScene()
            dispatch(setOpenCanvasSettings(true))
        }
    }

    const onDelete = async () => {
        dispatch(setOpenDeleteAlert(false))
        if (graphic.data._id) {
            await dispatch(deleteGraphicAsync(graphic.data._id))
        }
        await dispatch(clearAndReset())
        navigate("/app/editor")
        dispatch(setOpenCanvasSettings(true))
    }

    const handleDelete = () => {
        dispatch(setOpenDeleteAlert(true))
        setOpenMenu(false)
    }

    const handleTestGraphicNoSaveContinues = () => {
        dispatch(setOpenAlertTestGraphicDialog(false))
        const link = `${config.testApp}?graphicId=${graphic.data._id}`
        window.open(link, "_blank")
    }
    const handleTestGraphicSaveContinues = () => {
        handleSaveShortCall(graphic.data, canvas, getRootObjects(), false).then((isSaved) => {
            if (!isSaved) {
                return
            }
            const link = `${config.testApp}?graphicId=${graphic.data._id}`
            window.open(link, "_blank")
            dispatch(setOpenAlertTestGraphicDialog(false))
        })
    }

    if (location.pathname === "/app/editor") {
        return (
            <>
                <Grid container flexDirection="row" alignItems="center">
                    <Grid item>
                        {!graphic.status ? (
                            <Skeleton variant="text" width={120} height={60} />
                        ) : (
                            <ButtonWithTooltip
                                tooltipTitle={t("menu:editDetail")}
                                buttonComponent={
                                    <span>
                                        <Button
                                            disabled={
                                                !graphic.data.name ||
                                                expiredOwnerCompanyLicense ||
                                                expiredParticipantCompanyLicense
                                            }
                                            variant="text"
                                            color="inherit"
                                            sx={{
                                                p: 1,
                                                backgroundColor: theme.palette.background.paper,
                                            }}
                                            onClick={handleOpenSaveForm}
                                        >
                                            <i>{!graphic.data.name ? t("menu:untitled") : ""}</i>
                                            {graphic.data.name ? graphic.data.name : ""}
                                        </Button>
                                    </span>
                                }
                                disabled={false}
                            />
                        )}
                    </Grid>
                    {!graphic.data._id ? (
                        <LoadingButton
                            disabled={hasNoObjects || !graphic.status}
                            size="large"
                            loading={!graphic.status}
                            loadingPosition="end"
                            variant="text"
                            onClick={handleOpenSaveForm}
                            tooltipTitle={t("menu:save") + (isWindows ? ` | Ctrl S` : ` | ⌘ S`)}
                            color="inherit"
                            endIcon={<SaveAsRoundedIcon />}
                        ></LoadingButton>
                    ) : (
                        <LoadingButton
                            disabled={disabledShortSave}
                            size="large"
                            loading={!graphic.status}
                            loadingPosition="end"
                            variant="text"
                            onClick={() =>
                                handleSaveShortCall(graphic.data, canvas, getRootObjects(), false)
                            }
                            tooltipTitle={t("menu:save") + (isWindows ? ` | Ctrl S` : ` | ⌘ S`)}
                            color="secondary"
                            endIcon={<SaveRoundedIcon />}
                        ></LoadingButton>
                    )}
                    <ButtonWithTooltip
                        tooltipTitle={t("menu:moreContextMenu")}
                        buttonComponent={
                            <IconButton
                                aria-label="menu"
                                size="large"
                                ref={anchorRef}
                                id="composition-button"
                                aria-controls={openMenu ? "composition-menu" : undefined}
                                aria-expanded={openMenu ? "true" : undefined}
                                aria-haspopup="true"
                                onClick={handleToggle}
                                disabled={
                                    expiredOwnerCompanyLicense || expiredParticipantCompanyLicense
                                }
                            >
                                <MoreVertRoundedIcon />
                            </IconButton>
                        }
                        disabled={false}
                    />
                    <Popper
                        id="editor-step-7"
                        open={openMenu || openMenuWizard}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        placement="bottom-start"
                        transition
                        disablePortal
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin:
                                        placement === "bottom-start" ? "left top" : "left bottom",
                                }}
                            >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuList
                                            autoFocusItem={openMenu}
                                            id="composition-menu"
                                            aria-labelledby="composition-button"
                                            onKeyDown={handleListKeyDown}
                                        >
                                            <MenuItem key="createNew" onClick={handleCreateNew}>
                                                <ListItemIcon>
                                                    <NoteAddIcon />
                                                </ListItemIcon>
                                                <ListItemText>{t("menu:createNew")}</ListItemText>
                                                <span style={{ fontSize: "12px" }}>
                                                    {isWindows ? "Ctrl Alt N" : "⌥ ⌘ N"}
                                                </span>
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem
                                                key="duplicate"
                                                onClick={handleDuplicate}
                                                disabled={!canDuplicate || !graphic.data._id}
                                            >
                                                <ListItemIcon>
                                                    <ContentCopyIcon />
                                                </ListItemIcon>
                                                <ListItemText>{t("menu:duplicate")}</ListItemText>
                                                <span style={{ fontSize: "12px" }}>
                                                    {isWindows ? "Ctrl Shift D" : "⇧ ⌘ D"}
                                                </span>
                                            </MenuItem>
                                            <Divider />
                                            <ShowVersionHistory
                                                label={t("menu:showVersionHistory")}
                                                disabled={!graphic.data._id}
                                                onClick={() => setOpenMenu(false)}
                                            />
                                            <Divider />
                                            <TestGraphic
                                                label={t("menu:testGraphic")}
                                                disabled={!graphic.data._id}
                                                onClick={() => setOpenMenu(false)}
                                            />
                                            <Divider />
                                            <Tooltip
                                                title={
                                                    !canExportPackage
                                                        ? t("menu:noExportPackagePermission")
                                                        : ""
                                                }
                                                disableHoverListener={canExportPackage}
                                            >
                                                <span>
                                                    <DownloadFile
                                                        label={t("menu:exportZip")}
                                                        graphic={graphic.data}
                                                        onClick={() => setOpenMenu(false)}
                                                        disabled={!canExportPackage}
                                                        pack
                                                    />
                                                </span>
                                            </Tooltip>
                                            <DownloadFile
                                                label={t("menu:exportHtml")}
                                                graphic={graphic.data}
                                                onClick={() => setOpenMenu(false)}
                                                disabled={!canExport}
                                                infoText={
                                                    isFreeLicense
                                                        ? t("menu:exportHtmlInfo")
                                                        : undefined
                                                }
                                            />

                                            {hasRole(UserRole.Admin) && (
                                                <UploadFile
                                                    label={t("menu:import")}
                                                    onClick={() => setOpenMenu(false)}
                                                    disabled={!hasRole(UserRole.Admin)}
                                                />
                                            )}
                                            <Divider />
                                            <ClearScene
                                                label={t("menu:clear")}
                                                onClick={() => setOpenMenu(false)}
                                                disabled={isClearSceneDisabled}
                                            />
                                            <DeleteGraphic
                                                label={t("menu:delete")}
                                                onClick={handleDelete}
                                                disabled={!graphic.data._id}
                                            />
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                    <AlertDialog
                        open={openDialogCreateNew && !graphic.data._id}
                        title={t("editor:discardCreateNewTitle")}
                        content={t("editor:discardContent")}
                        onDisagree={() => dispatch(setOpenDialogCreateNew(false))}
                        onAgree={handleAgreeAlertDialog}
                        txtDisagree={t("disagree")}
                        txtAgree={t("leave")}
                    ></AlertDialog>
                    <AlertMoreChoiceDialog
                        open={
                            (openDialogToDuplicate && (canUndo || canRedo)) ||
                            (openDialogCreateNew && (canUndo || canRedo))
                        }
                        title={`${t("editor:titleUnsavedChanges")} '${
                            graphic.data.name ? graphic.data.name : t("menu:untitled")
                        }' ?`}
                        content={t("editor:contentUnsavedChanges")}
                        onClose={() => handleCloseAlert3ChoiceDialog()}
                        buttonArray={[
                            {
                                key: "disagree",
                                onclick: () => handleCloseAlert3ChoiceDialog(),
                                txt: t("disagree"),
                                variant: "text",
                                color: "inherit",
                            },
                            {
                                key: "continue-without-saving",
                                onclick: () => handleContinueWithoutSaving(),
                                txt: t("editor:continueWithoutSaving"),
                                variant: "text",
                                color: "inherit",
                            },
                            {
                                key: "save-changes",
                                onclick: () =>
                                    handleSaveAndContinue(graphic.data, canvas, getRootObjects()),
                                txt: t("editor:saveChanges"),
                                variant: "contained",
                                color: "primary",
                                disabled: !Boolean(graphic.data._id),
                            },
                        ]}
                    />
                    <AlertTestGraphicDialog
                        noSaveContinues={handleTestGraphicNoSaveContinues}
                        saveContinues={handleTestGraphicSaveContinues}
                    />
                    <AlertDialog
                        open={openDelAlert}
                        title={`${t("graphics:delete")} '${graphic.data.name}' ?`}
                        content={t("graphics:deleteQuestion")}
                        onDisagree={() => dispatch(setOpenDeleteAlert(false))}
                        onAgree={() => onDelete()}
                        txtDisagree={t("disagree")}
                        txtAgree={t("delete")}
                        colorAgree="error"
                    />
                    <AlertDialog
                        open={openLockedDialog}
                        title={t("graphics:lockedDialogs.isFinal.title")}
                        content={t("graphics:lockedDialogs.isFinal.content")}
                        txtAgree={t("agree")}
                        onAgree={() => {
                            setOpenLockedDialog(false)
                            navigate("/app/graphics")
                        }}
                    />
                    <AlertDialog
                        open={openLockedSaveDialog}
                        title={t("graphics:lockedDialogs.saveAttempt.title")}
                        content={t("graphics:lockedDialogs.saveAttempt.content")}
                        txtAgree={t("disagree")}
                        onAgree={() => {
                            setOpenLockedSaveDialog(false)
                            navigate("/app/graphics")
                        }}
                    />
                </Grid>
                <LoadingBackdrop open={!graphic.status} />
            </>
        )
    } else {
        return null
    }
}

export default TopNavbarEditorActions
